// Rules of the Road dialog component
import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Button,
  Link,
  Text,
} from '@fluentui/react-components';
import { Box, useFluentTokens } from '../../../theme/fluent';
import { analyticsService } from '../../../services/analyticsService';

interface RulesOfTheRoadDialogProps {
  open: boolean;
  onAccept: () => void;
}

export const RulesOfTheRoadDialog: React.FC<RulesOfTheRoadDialogProps> = ({ open, onAccept }) => {
  const { tokens, modeTokens } = useFluentTokens();

  const rulesUrl =
    'https://ukdeloitte.sharepoint.com/sites/DigitalAssistantPilotResources/Shared%20Documents/Resources/Rules%20of%20the%20Road/DA%20Pilot%20-%20Rules%20of%20the%20Road%20Appendix.pdf';
  const copilotGuidanceUrl =
    'https://resources.deloitte.com/sites/dr-uk/SitePages/Firm/Programmes/Guidance%20for%20AI-excluded%20clients.aspx';

  const handleAccept = () => {
    analyticsService.trackEvent('rules_of_road_accepted');
    onAccept();
  };

  return (
    <Dialog open={open} modalType="modal">
      <DialogSurface
        style={{
          width: 'calc(100% - 32px)',
          maxWidth: '600px',
          borderRadius: tokens.borderRadiusXXXLarge,
          maxHeight: `calc(100% - (2 * ${tokens.spacingVerticalXL}))`,
          overflowY: 'auto',
        }}
      >
        <DialogBody>
          <DialogTitle
            style={{
              fontSize: tokens.fontSizeBase500,
              fontWeight: tokens.fontWeightSemibold,
              color: modeTokens.colorNeutralForeground1,
              marginBottom: tokens.spacingVerticalXXS,
            }}
          >
            Welcome to Robin
          </DialogTitle>
          <DialogContent
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: tokens.spacingVerticalSNudge,
              overflow: 'visible',
            }}
          >
            <Box display="flex" flexDirection="column" style={{ gap: tokens.spacingVerticalXXS }}>
              {/* Main intro paragraph */}
              <Text
                style={{
                  fontSize: tokens.fontSizeBase300,
                  lineHeight: tokens.lineHeightBase300,
                  color: modeTokens.colorNeutralForeground1,
                }}
              >
                Before using this application, please confirm that you have read and adhere to the updated Rules of the
                Road by clicking accept. We've highlighted below the important do's and don'ts within the Rules of the
                Road, when using this tool:
              </Text>

              {/* Do input/upload section */}
              <Box display="flex" flexDirection="column" gap="xxs" style={{ marginTop: tokens.spacingVerticalS }}>
                <Text
                  weight="semibold"
                  style={{
                    fontSize: tokens.fontSizeBase200,
                    lineHeight: tokens.lineHeightBase200,
                    color: modeTokens.colorNeutralForeground1,
                  }}
                >
                  Do input/upload:
                </Text>
                <Box
                  as="ul"
                  style={{
                    margin: 0,
                    paddingLeft: tokens.spacingHorizontalL,
                    listStyleType: 'disc',
                  }}
                >
                  <li style={{ fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 }}>
                    <Text weight="semibold" style={{ fontSize: tokens.fontSizeBase200 }}>
                      Public or Synthetic data
                    </Text>
                    <Text style={{ fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 }}>
                      {
                        ' (which is free to use in the public domain other than Personal Data, or for which a license has been obtained – '
                      }
                    </Text>
                    <Link
                      href={rulesUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: modeTokens.rulesDialogLinkColor,
                        fontSize: tokens.fontSizeBase200,
                      }}
                    >
                      see Appendix
                    </Link>
                    <Text style={{ fontSize: tokens.fontSizeBase200 }}>)</Text>
                  </li>
                  <li style={{ fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 }}>
                    <Text weight="semibold" style={{ fontSize: tokens.fontSizeBase200 }}>
                      Deloitte data
                    </Text>
                    <Text style={{ fontSize: tokens.fontSizeBase200 }}> classified as </Text>
                    <Text weight="semibold" style={{ fontSize: tokens.fontSizeBase200 }}>
                      Public or Confidential.
                    </Text>
                  </li>
                  <li style={{ fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 }}>
                    <Text weight="semibold" style={{ fontSize: tokens.fontSizeBase200 }}>
                      Client Data
                    </Text>
                    <Text style={{ fontSize: tokens.fontSizeBase200 }}> (</Text>
                    <Text weight="semibold" style={{ fontSize: tokens.fontSizeBase200 }}>
                      Excluding High Risk Confidential
                    </Text>
                    <Text style={{ fontSize: tokens.fontSizeBase200 }}>
                      ) that have undergone a check before use to ensure compliance.
                    </Text>
                  </li>
                </Box>
              </Box>

              {/* Extra condition section */}
              <Box display="flex" flexDirection="column" gap="xxs" style={{ marginTop: tokens.spacingVerticalS }}>
                <Text
                  weight="semibold"
                  style={{
                    fontSize: tokens.fontSizeBase200,
                    lineHeight: tokens.lineHeightBase200,
                    color: modeTokens.colorNeutralForeground1,
                  }}
                >
                  Extra condition:
                </Text>
                <Text style={{ fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 }}>
                  From time to time, clients do state that they do not want their data to be input or uploaded onto AI
                  platforms; and this is contractually articulated.
                </Text>
                <Text
                  style={{
                    fontSize: tokens.fontSizeBase200,
                    lineHeight: tokens.lineHeightBase200,
                    marginTop: tokens.spacingVerticalS,
                    marginBottom: tokens.spacingVerticalS,
                  }}
                >
                  Please ensure, that such client data, under contract, is not to be input onto an AI platform (Ensure,
                  by checking with Partner's, which clients disallow this).
                </Text>
              </Box>

              {/* Do not input/upload section */}
              <Box display="flex" flexDirection="column" gap="xxs">
                <Text
                  weight="semibold"
                  style={{
                    fontSize: tokens.fontSizeBase200,
                    lineHeight: tokens.lineHeightBase200,
                    color: modeTokens.colorNeutralForeground1,
                  }}
                >
                  Do not input/upload:
                </Text>
                <Box
                  as="ul"
                  style={{
                    margin: 0,
                    paddingLeft: tokens.spacingHorizontalL,
                    listStyleType: 'disc',
                  }}
                >
                  <li style={{ fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 }}>
                    <Text weight="semibold" style={{ fontSize: tokens.fontSizeBase200 }}>
                      Deloitte data
                    </Text>
                    <Text style={{ fontSize: tokens.fontSizeBase200 }}> classified as </Text>
                    <Text weight="semibold" style={{ fontSize: tokens.fontSizeBase200 }}>
                      High-Risk Confidential
                    </Text>
                    <Text style={{ fontSize: tokens.fontSizeBase200 }}>.</Text>
                  </li>
                  <li style={{ fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 }}>
                    <Text weight="semibold" style={{ fontSize: tokens.fontSizeBase200 }}>
                      Client Data
                    </Text>
                    <Text style={{ fontSize: tokens.fontSizeBase200 }}> classified as </Text>
                    <Text weight="semibold" style={{ fontSize: tokens.fontSizeBase200 }}>
                      High-Risk Confidential.
                    </Text>
                  </li>
                  <li style={{ fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 }}>
                    <Text weight="semibold" style={{ fontSize: tokens.fontSizeBase200 }}>
                      Client Data
                    </Text>
                    <Text style={{ fontSize: tokens.fontSizeBase200 }}>
                      {' '}
                      belonging to clients where the contract/agreement{' '}
                    </Text>
                    <Text weight="semibold" style={{ fontSize: tokens.fontSizeBase200 }}>
                      prohibits the use of AI Tools.{' '}
                    </Text>
                    <Text style={{ fontSize: tokens.fontSizeBase200 }}>Apply the principles of the </Text>
                    <Link
                      href={copilotGuidanceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: modeTokens.rulesDialogLinkColor,
                        fontSize: tokens.fontSizeBase200,
                      }}
                    >
                      Copilot Guidance for AI-Excluded Clients
                    </Link>
                    <Text style={{ fontSize: tokens.fontSizeBase200 }}>.</Text>
                  </li>
                  <li style={{ fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 }}>
                    <Text weight="semibold" style={{ fontSize: tokens.fontSizeBase200 }}>
                      Personal data
                    </Text>
                    <Text style={{ fontSize: tokens.fontSizeBase200 }}> beyond Business Contact Data (</Text>
                    <Link
                      href={rulesUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: modeTokens.rulesDialogLinkColor,
                        fontSize: tokens.fontSizeBase200,
                      }}
                    >
                      See Appendix
                    </Link>
                    <Text style={{ fontSize: tokens.fontSizeBase200 }}>)</Text>
                  </li>
                </Box>
              </Box>

              {/* Final paragraph */}
              <Text
                style={{
                  fontSize: tokens.fontSizeBase300,
                  lineHeight: tokens.lineHeightBase300,
                  color: modeTokens.colorNeutralForeground1,
                  marginTop: tokens.spacingVerticalS,
                }}
              >
                Robin uses AI, please check outputs for accuracy.
              </Text>
            </Box>

            <Box
              display="flex"
              justifyContent="flex-end"
              style={{
                paddingTop: tokens.spacingVerticalXS,
              }}
            >
              <Button
                appearance="primary"
                size="large"
                onClick={handleAccept}
                style={{
                  borderRadius: tokens.borderRadiusXXXXLarge,
                  fontWeight: tokens.fontWeightSemibold,
                  minWidth: '96px',
                }}
              >
                Accept
              </Button>
            </Box>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
